
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import { State } from 'vuex-class';
import { PrescriptionAction, PrescriptionMutation } from '@/store/Prescription/PrescriptionRoot';

// Api
import { createCall, getCallById, approveCall, toWorkCall, acceptCall } from '@/utils/api/Prescriptions';

// Interfaces
import User from '@/ship/Models/User';
import IncludeCall from '@/ship/Models/IncludeCall';
import UserService from '@/utils/UserService';
import { CreateFormData } from '@/ship/Models/CallLog';
import IModalEvent from '@/ship/Models/IModalEvent';
import IDictionary from '@/ship/Models/IDictionary';

// Enums
import ModalEnum from '@/ship/Enums/ModalEnum';
import { StatusEnum } from '@/ship/Enums/StatusEnum';
import { CallLogViolationEnum } from '@/ship/Enums/CallLogEnum';

// Components
import { VGridCol, VGridRow, VGridScroll } from 'vui';
import VSpinner from '@/components/VSpinner.vue';
import VButton from '@/components/VButton/VButton.vue';
import ErrorViewer from '@/components/ErrorViewer/ErrorViewer.vue';
import CallLogForm from '@/components/SelectorsForm/CallLogForm.vue';
import VButtonBack from '@/components/VButton/VButtonBack.vue';
import VPrescriptionHistory from '@/components/Prescription/VPrescriptionHistory.vue';
import Violation from '../Violation/Violation.vue';
import VModalGeneral from '@/components/VModal/VModalGeneral.vue';

@Component({
    components: {
        ErrorViewer,
        VGridCol,
        VGridRow,
        VButton,
        VGridScroll,
        CallLogForm,
        VSpinner,
        VButtonBack,
        VPrescriptionHistory,
        Violation,
        VModalGeneral,
    },
    beforeRouteLeave(to, from, next) {
        const isCreateCall = (this as CallLog).isCreateCall;
        const isCreatedCall = (this as CallLog).isCreatedCall;
        if (isCreateCall && !isCreatedCall) {
            (this as CallLog).showLeaveModal();
            this.$nextTick(() => {
                this.$refs.leaveModal?.$once('before-close', (bvEvt: IModalEvent) => {
                    if (bvEvt.params?.isLeaving) {
                        this.$refs.leaveModal?.$once('closed', () => {
                            next();
                        });
                    } else {
                        next(false);
                    }
                });
            });
        } else {
            next();
        }
    },
})
export default class CallLog extends Vue {
    @Ref() leaveModal!: VModalGeneral;
    @PrescriptionMutation RESET_CALLS!: () => void;
    @PrescriptionAction GET_ALL_CALLS!: (param: { page: number; query: string }) => Promise<IncludeCall[]>;

    @State user!: User;

    @Prop({ type: Number, default: 0 }) selectorKey!: number;
    @Prop() callId!: number | string;
    @Prop({ type: Boolean, default: true }) isCreateCall!: boolean;

    CallLogViolationEnum = CallLogViolationEnum;

    call: IncludeCall | null = null;
    isDataLoad: boolean = false;
    StatusEnum = StatusEnum;
    UserService = UserService;
    isHistoryShown = false;
    isDisabledCreateButton = true;
    isDisabledApproveButton = true;
    isDisabledToWorkButton = true;
    requestType: CallLogViolationEnum | null = null;
    formData: CreateFormData = {
        project: null,
        customer: '',
        general: '',
        subcontractor: null,
        author: '',
        address: '',
        description: '',
        photo: null,
        responsible: null,
        executor: null,
    };
    toggle = false;
    isCreatedCall = false;

    get hasAccess() {
        if (
            UserService.isGeneralContractor(this.user) ||
            UserService.isSubContractor(this.user) ||
            UserService.isCustomer(this.user)
        ) {
            return true;
        }
        return null;
    }

    get canApproveCall() {
        return (
            UserService.isGeneralContractor(this.user) &&
            this.call?.status === StatusEnum.Created &&
            !this.call.responsibleId
        );
    }

    get canToWorkCall() {
        return (
            UserService.isCustomer(this.user) &&
            this.call?.status === StatusEnum.Created &&
            this.call?.responsible?.data.id === this.user.id
        );
    }

    get canAcceptCall() {
        return UserService.isCustomer(this.user) && this.call?.status === StatusEnum.InWork;
    }

    created() {
        if (this.callId) {
            this.checkCall(Number(this.callId));
        }
    }

    @Watch('callId')
    async checkCall(callId: number) {
        this.call = null;
        this.resetFormData();
        this.isCreatedCall = false;
        if (!this.callId) {
            return;
        }

        this.isDataLoad = false;

        await getCallById(callId)
            .then((call) => {
                this.call = call.data.data;
                this.isDataLoad = true;
            })
            .catch(() => {
                this.isDataLoad = false;
            });
    }

    updateFormData(event: CreateFormData) {
        this.formData = { ...this.formData, ...event };
        this.updateButtonState();
    }

    updatePhoto(photo: { id: number[]; value: string[] }) {
        this.formData.photo = photo;
        this.updateButtonState();
    }

    updateButtonState() {
        const _description = this.formData.description.replace(/\n/g, '');

        if (!this.formData.project || !this.formData.address || !_description) {
            this.isDisabledCreateButton = true;
        } else {
            this.isDisabledCreateButton = false;
        }

        if (!this.formData.responsible) {
            this.isDisabledApproveButton = true;
        } else {
            this.isDisabledApproveButton = false;
        }

        if (!this.formData.executor) {
            this.isDisabledToWorkButton = true;
        } else {
            this.isDisabledToWorkButton = false;
        }
    }

    updateToggle(event: boolean) {
        this.toggle = event;
    }

    showModalError() {
        this.$modal.show(ModalEnum.Alert, {
            title: this.$t('page.modal.alert.requestFail'),
            description: this.$t('page.modal.alert.pleaseTryAgain'),
            hideCloseIcon: false,
        });
    }

    showModalApprove() {
        this.$modal.show(ModalEnum.Alert, {
            title: `Заявка на вызов №${this.call?.id} согласована и отправлена ответственному.`,
            actionLabel: this.$t('page.modal.answer.understand'),
        });
    }

    resetTable() {
        this.RESET_CALLS();
        this.GET_ALL_CALLS({ page: 1, query: '' });
    }

    resetFormData() {
        this.formData = {
            project: null,
            customer: '',
            general: '',
            subcontractor: null,
            author: '',
            address: '',
            description: '',
            photo: null,
            responsible: null,
            executor: null,
        };
    }

    reset() {
        this.checkCall(Number(this.callId));
        this.resetTable();
        this.resetFormData();
        this.toggle = false;
    }

    createCall() {
        if (this.formData.project && this.formData.address && this.formData.description) {
            createCall({
                project_id: this.formData.project.id,
                subcontractor_id: this.formData.subcontractor?.id,
                request_author: this.formData.author,
                request_address: this.formData.address,
                request_description: this.formData.description,
                photo: this.formData.photo?.id,
            })
                .then((call) => {
                    this.isCreatedCall = true;
                    this.RESET_CALLS();

                    this.$router.replace({
                        name: 'calllog.view',
                        params: {
                            callId: call.data.data.id,
                        },
                    });
                })
                .catch(() => {
                    this.showModalError();
                });
        }
    }

    approveCall() {
        if (this.formData.responsible && this.call) {
            approveCall({
                id: this.call.id,
                responsible_id: this.formData.responsible.id,
            })
                .then(() => {
                    this.showModalApprove();
                    this.reset();
                })
                .catch(() => {
                    this.showModalError();
                });
        }
    }

    toWorkCall() {
        if (this.formData.executor && this.call) {
            toWorkCall({
                id: this.call.id,
                executor_id: this.formData.executor.id,
            })
                .then(() => {
                    this.reset();
                })
                .catch(() => {
                    this.showModalError();
                });
        }
    }

    cancelCall() {
        this.requestType = CallLogViolationEnum.Cancel;
        this.toggle = true;
    }

    rejectCall() {
        this.requestType = CallLogViolationEnum.Reject;
        this.toggle = true;
    }

    acceptCall() {
        if (this.call) {
            acceptCall(this.call.id)
                .then(() => {
                    this.reset();
                })
                .catch(() => {
                    this.showModalError();
                });
        }
    }

    handleLeaveModal() {
        this.hideLeaveModal({ isLeaving: true });
    }

    hideLeaveModal(params?: IDictionary) {
        this.leaveModal.hideModal(params);
    }

    showLeaveModal() {
        this.leaveModal.showModal();
    }

    emitCloseSecondaryPage() {
        this.$emit('emitCloseSecondaryPage');
        this.$router.push({
            name: 'prescription.calllog',
            query: this.$route.query,
        });
    }
}
